import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { YStack } from "tamagui";
import AuthScaffold, { AuthDivider, AuthFooterText, AuthInfoPanel } from "@/components/AuthScaffold";
import { supabase } from "@/integrations/supabase/client";
import { getRememberSessionPreference } from "@/lib/authPersistence";
import { beginWebSocialAuth } from "@/lib/socialAuth";
import {
  buildWebAuthPath,
  getWebReturnToFromSearch,
  rememberWebPostAuthRedirect,
} from "@/lib/postAuthRedirect";
import { toast } from "sonner";
import { notifySignupAutomation } from "@/lib/signupWebhook";
import { AppButton, AppCheckbox, AppFieldGroup, AppInput, AppText } from "../../ui-foundation";
import { EMAIL_CONFIRMATION_SIGNUP_WEB_MESSAGE } from "../../shared/auth/emailConfirmation";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.26 9.55c-.02-2.29 1.87-3.4 1.96-3.45-1.07-1.57-2.74-1.78-3.33-1.81-1.42-.14-2.77.84-3.49.84-.72 0-1.83-.82-3.01-.8-1.54.02-2.97.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.77 1.11 1.68 2.35 2.88 2.31 1.16-.05 1.59-.75 2.99-.75 1.4 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.24.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.43-.93-2.45-3.68zM11.98 2.9c.64-.78 1.07-1.86.95-2.94-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.77-.97 2.82 1.02.08 2.06-.52 2.71-1.26z" fill="currentColor"/>
  </svg>
);

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getWebReturnToFromSearch(location.search);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (returnTo) {
      rememberWebPostAuthRedirect(returnTo);
    }
  }, [returnTo]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!displayName.trim()) nextErrors.displayName = "Please enter your name";
    if (!email.trim()) nextErrors.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) nextErrors.email = "That doesn't look like a valid email";
    if (!password) nextErrors.password = "Please choose a password";
    else if (password.length < 6) nextErrors.password = "Password needs to be at least 6 characters";
    if (!agreedToTerms) nextErrors.agreedToTerms = "Please agree to the Terms and Privacy Policy";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleGoogleSignUp = async () => {
    if (!agreedToTerms) {
      setErrors((current) => ({
        ...current,
        agreedToTerms: "Please agree to the Terms and Privacy Policy",
      }));
      return;
    }

    setGoogleLoading(true);

    try {
      await beginWebSocialAuth({
        provider: "google",
        intent: "signup",
        rememberSession: getRememberSessionPreference(),
        fallbackDisplayName: displayName,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Google sign-up failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    if (!agreedToTerms) {
      setErrors((current) => ({
        ...current,
        agreedToTerms: "Please agree to the Terms and Privacy Policy",
      }));
      return;
    }

    setAppleLoading(true);

    try {
      await beginWebSocialAuth({
        provider: "apple",
        intent: "signup",
        rememberSession: getRememberSessionPreference(),
        fallbackDisplayName: displayName,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Apple sign-up failed");
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: `${window.location.origin}${buildWebAuthPath("/signin", returnTo)}`,
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      void notifySignupAutomation({
        userId: data.user?.id ?? null,
        email: email.trim(),
        displayName: displayName.trim(),
        signupMethod: "email",
        platform: "web",
      });

      if (data.session) {
        await supabase.auth.signOut();
      }

      toast.success(EMAIL_CONFIRMATION_SIGNUP_WEB_MESSAGE);
      navigate(buildWebAuthPath("/signin", returnTo));
    }
  };

  return (
    <AuthScaffold
      eyebrow="Join Kindred"
      title="Start with a warm, clear welcome."
      description="Create your account, confirm your email, and we’ll guide you into the parts of Kindred that feel most useful right now."
    >
      <YStack tag="form" onSubmit={handleSignUp} gap="$sm">
        <AppFieldGroup label={<label htmlFor="displayName">Your name</label>} error={errors.displayName}>
          <AppInput
            id="displayName"
            value={displayName}
            onChangeText={setDisplayName}
            placeholder="What should we call you?"
            autoComplete="name"
          />
        </AppFieldGroup>

        <AppFieldGroup label={<label htmlFor="email">Email</label>} error={errors.email}>
          <AppInput
            id="email"
            type="email"
            value={email}
            onChangeText={setEmail}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </AppFieldGroup>

        <AppFieldGroup label={<label htmlFor="password">Password</label>} error={errors.password}>
          <AppInput
            id="password"
            type="password"
            value={password}
            onChangeText={setPassword}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
        </AppFieldGroup>

        <AuthInfoPanel>
          <AppCheckbox
            id="agreedToTerms"
            checked={agreedToTerms}
            onCheckedChange={(nextChecked) => {
              setAgreedToTerms(nextChecked);
              setErrors((current) => {
                const nextErrors = { ...current };
                delete nextErrors.agreedToTerms;
                return nextErrors;
              });
            }}
            label={
              <span style={{ flex: 1 }}>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  I agree to the{" "}
                  <Link to="/terms" className="kindred-inline-link">
                    Terms &amp; Conditions
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="kindred-inline-link">
                    Privacy Policy
                  </Link>
                  .
                </AppText>
              </span>
            }
          />
          {errors.agreedToTerms ? (
            <AppText color="$destructive" fontSize="$sm" lineHeight="$sm">
              {errors.agreedToTerms}
            </AppText>
          ) : null}
        </AuthInfoPanel>

        <AppButton type="submit" disabled={loading} width="100%">
          {loading ? "Creating account..." : "Create account"}
        </AppButton>

        <AuthDivider />

        <YStack gap="$xs">
          <AppButton
            type="button"
            tone="outline"
            onPress={handleGoogleSignUp}
            disabled={googleLoading || appleLoading}
            width="100%"
          >
            <GoogleIcon />
            {googleLoading ? "Starting Google..." : "Continue with Google"}
          </AppButton>
          <AppButton
            type="button"
            tone="outline"
            onPress={handleAppleSignUp}
            disabled={googleLoading || appleLoading}
            width="100%"
          >
            <AppleIcon />
            {appleLoading ? "Starting Apple..." : "Continue with Apple"}
          </AppButton>
        </YStack>
      </YStack>

      <AuthFooterText>
        By joining, you agree to our{" "}
        <Link to="/terms" className="kindred-inline-link">
          Terms &amp; Conditions
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="kindred-inline-link">
          Privacy Policy
        </Link>
        .
      </AuthFooterText>

      <AuthFooterText>
        Already a member?{" "}
        <Link to={buildWebAuthPath("/signin", returnTo)} className="kindred-inline-link">
          Sign in
        </Link>
      </AuthFooterText>
    </AuthScaffold>
  );
};

export default SignUp;
